var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageContainer, PageContent } from "../../components/wraper";
import { PrimaryGrid } from "../../components/grid/PrimaryGrid";
import { GridFilters } from "../../components/grid/GridFilters";
import PageHeader from "@ui/components/header/PageHeader";
import TemperatureDisplay from "@ui/components/temperature/TemperatureDisplay";
import { TemperatureStatusBadge, getNodeTemperatureStatus, getNodeTemperatureColor, } from "@ui/components/temperature";
import { StatusIndicator } from "@ui/components/status";
import { SButton } from "@ui/components/buttons/SButton";
import { colCenter } from "@ui/components/style/style";
import { FreeRoamingBadge } from "@ui/components/badges/FreeRoamingBadge";
import { Permission, PermissionChecker } from "@utility/permission-checker";
import { useDebounce } from "@ui/utils/useDebounce";
import { nodesApi } from "@api/enpoints/nodes-api";
import { useNodesActions } from "../nodes/hooks/useNodesActions";
const mapRow = (node) => ({
    id: node.id,
    nodeId: node.nodeUid,
    nodeName: node.nodeName || "",
    deviceId: node.accessPointUid || "",
    deviceName: node.accessPointName || "",
    actualDeviceId: node.actualAccessPointUid || "",
    status: node.isActive ? "active" : "inactive",
    isActive: node.isActive,
    nodeTemperature: node.nodeTemprature,
    lowTemperature: node.lowTemperature,
    highTemperature: node.highTemperature,
    nodeType: node.nodeTypeCode,
    nodeTypeCode: node.nodeTypeCode,
    lastUpdate: node.lastUpdated,
});
/**
 * One line inside the connection popup. Rendered with plain boxes rather than MUI Chips
 * because the tooltip surface is dark grey — chip "soft" variants would be dark on dark.
 */
const ConnectionRow = ({ label, uid, emptyLabel, highlight = false }) => (React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1, mb: 0.5 } },
    React.createElement(Typography, { variant: "caption", sx: { opacity: 0.75, minWidth: 84 } }, label),
    React.createElement(Typography, { variant: "body2", sx: {
            fontFamily: "monospace",
            opacity: uid ? 1 : 0.6,
            color: uid && highlight ? "warning.light" : "inherit",
        } }, uid || emptyLabel)));
/**
 * Single "Connection" cell replacing the old pair of AP id columns.
 *
 * "Assigned AP" is the simulated connection (the AP an operator assigned the node to);
 * "Current AP" is the actual connection (the AP physically relaying it right now).
 *
 * Both ids are long hex strings that crowded the grid, so the cell carries only a state-
 * coloured badge and reveals the detail on hover: amber when the node has roamed away from
 * its assigned AP, green when it is where it belongs, grey when nothing has relayed it yet.
 */
const ConnectionDetailsCell = ({ simulated, actual }) => {
    const { t } = useTranslation(["common"]);
    const roamedAway = !!actual && !!simulated && actual !== simulated;
    const tone = !actual
        ? "default"
        : roamedAway
            ? "warning"
            : "success";
    const popup = (React.createElement(Box, { sx: { py: 0.5, minWidth: 240 } },
        React.createElement(Typography, { variant: "subtitle2", sx: { mb: 0.75, fontWeight: 600 } }, t("common:grid.connectionDetails")),
        React.createElement(ConnectionRow, { label: t("common:grid.assignedAp"), uid: simulated, emptyLabel: "\u2014" }),
        React.createElement(ConnectionRow, { label: t("common:grid.currentAp"), uid: actual, emptyLabel: t("common:grid.notConnected"), highlight: roamedAway }),
        !actual && (React.createElement(Typography, { variant: "caption", sx: { display: "block", opacity: 0.75 } }, t("common:descriptions.freeRoamingNoActualAp")))));
    return (React.createElement(Tooltip, { title: popup, arrow: true, placement: "top", enterTouchDelay: 0 },
        React.createElement(Chip, { label: t("common:grid.connectionDetails"), size: "small", variant: "soft", color: tone, sx: { cursor: "default" } })));
};
const PAGE_SIZE_OPTIONS = [25, 50];
const FreeRoamingNodesPage = () => {
    const { t } = useTranslation(["common"]);
    const navigate = useNavigate();
    const { handleEditNode, handleConfigureAPDevice } = useNodesActions();
    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
    const [searchTerm, setSearchTerm] = useState("");
    const [filters, setFilters] = useState({});
    const debouncedSearch = useDebounce(searchTerm, 300);
    const fetchNodes = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        setLoading(true);
        try {
            const response = yield nodesApi.getAllNodes({
                pageNumber,
                pageSize,
                sortColumn: "nodeName",
                sortDirection: "asc",
                filters: Object.assign(Object.assign({}, filters), { globalSearch: debouncedSearch || undefined, freeRoamingOnly: true }),
            });
            const page = (_a = response.data) === null || _a === void 0 ? void 0 : _a.data;
            setRows(((_b = page === null || page === void 0 ? void 0 : page.items) !== null && _b !== void 0 ? _b : []).map(mapRow));
            setTotalCount((_c = page === null || page === void 0 ? void 0 : page.totalCount) !== null && _c !== void 0 ? _c : 0);
        }
        catch (_d) {
            setRows([]);
            setTotalCount(0);
        }
        finally {
            setLoading(false);
        }
    }), [pageNumber, pageSize, filters, debouncedSearch]);
    useEffect(() => {
        fetchNodes();
    }, [fetchNodes]);
    const handleFilterChange = useCallback((newFilters) => {
        setFilters(newFilters);
        setPageNumber(1);
    }, []);
    const columns = useMemo(() => [
        {
            field: "node",
            headerName: t("common:grid.node"),
            minWidth: 100,
            filterable: false,
            renderCell: (params) => {
                var _a;
                return (React.createElement(Box, { sx: Object.assign({ height: "100%" }, colCenter) },
                    React.createElement(TemperatureDisplay, Object.assign({ width: 91, temperature: (_a = params.row.nodeTemperature) !== null && _a !== void 0 ? _a : null }, (params.row.nodeTypeCode === 1
                        ? { leftIndicator: 1, rightIndicator: 2 }
                        : {}), { color: getNodeTemperatureColor(params.row) }))));
            },
        },
        {
            field: "nodeId",
            headerName: t("common:grid.nodeUid"),
            minWidth: 110,
            filterable: false,
        },
        {
            // Node name plus its status badges, all in one cell — rather than
            // "temperature" being its own column — so the gap between the two badges
            // is a fixed, deliberate spacing rather than whatever leftover width the
            // column-width calculation happens to hand each one on a given row.
            //
            // The name sits in a `flex: 1` box rather than sizing to its own text, so
            // it always claims exactly "cell width minus the two fixed-size badges" —
            // identical on every row, since every row's cell is the same computed
            // width — which keeps the badges lined up at the same x position row to
            // row instead of drifting with each name's length.
            field: "nodeName",
            headerName: t("common:grid.nodeName"),
            flex: 2,
            minWidth: 220,
            filterable: false,
            renderCell: (params) => (React.createElement(Stack, { direction: "row", spacing: 1, alignItems: "center", sx: { height: "100%", width: "100%", minWidth: 0 } },
                React.createElement(Box, { sx: { flex: 1, minWidth: 0 } },
                    React.createElement(Tooltip, { title: params.row.nodeName },
                        React.createElement(Typography, { variant: "body2", noWrap: true, sx: { overflow: "hidden", textOverflow: "ellipsis" } }, params.row.nodeName))),
                React.createElement(Box, { sx: { flexShrink: 0 } },
                    React.createElement(FreeRoamingBadge, null)),
                React.createElement(Box, { sx: { flexShrink: 0 } },
                    React.createElement(TemperatureStatusBadge, { status: getNodeTemperatureStatus(params.row) })))),
        },
        {
            field: "status",
            headerName: t("common:grid.status"),
            flex: 1,
            minWidth: 100,
            filterable: false,
            renderCell: (params) => (React.createElement(StatusIndicator, { variant: params.row.isActive ? "active" : "inactive" })),
        },
        {
            field: "connection",
            headerName: t("common:grid.connection"),
            flex: 1,
            minWidth: 150,
            filterable: false,
            sortable: false,
            renderCell: (params) => (React.createElement(ConnectionDetailsCell, { simulated: params.row.deviceId, actual: params.row.actualDeviceId })),
        },
        {
            field: "actions",
            headerName: t("common:grid.actions"),
            headerAlign: "center",
            minWidth: 240,
            filterable: false,
            renderCell: (params) => {
                var _a;
                return (React.createElement(Stack, { direction: "row", spacing: 1, alignItems: "center" },
                    React.createElement(SButton, { size: "medium", variant: "soft", onClick: () => {
                            var _a;
                            // Always the simulated AP. Every AP-scoped lookup in the app (node list,
                            // dropdowns, replacement history) resolves through simulatedConnection, so
                            // the actual AP would open a page that does not list this node.
                            const ap = (_a = params.row.deviceId) === null || _a === void 0 ? void 0 : _a.trim();
                            navigate(ap
                                ? `/nodes/${params.row.nodeId}/logs/${ap}`
                                : `/nodes/${params.row.nodeId}/logs`);
                        } }, t("common:actions.logs")),
                    PermissionChecker.hasPermission(Permission.NODES_EDIT) && (React.createElement(SButton, { variant: "soft", color: "primary", size: "medium", onClick: () => handleEditNode(params.row.id) }, t("common:actions.edit"))),
                    PermissionChecker.hasPermission(Permission.ACCESSPOINTS_EDIT) && (React.createElement(SButton, { variant: "outlined", color: "primary", size: "medium", onClick: () => handleConfigureAPDevice(params.row.deviceId), disabled: !((_a = params.row.deviceId) === null || _a === void 0 ? void 0 : _a.trim()) }, "AP"))));
            },
        },
    ], [t, navigate, handleEditNode, handleConfigureAPDevice]);
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: t("common:pages.freeRoamingNodes") }),
        React.createElement(PageContent, null,
            React.createElement(GridFilters, { title: `${totalCount} ${t("common:pages.freeRoamingNodes")}`, loading: loading, columns: columns, filters: filters, onChange: handleFilterChange, searchProp: searchTerm, handleSearchChange: (event) => {
                    setSearchTerm(event.target.value);
                    setPageNumber(1);
                }, onApplyFilters: fetchNodes }),
            React.createElement(PrimaryGrid, { rows: loading ? [] : rows, columns: columns, loading: loading, heightVariant: "full", checkboxSelection: false, autoHeight: true, paginationMode: "server", rowCount: totalCount, paginationModel: { page: pageNumber - 1, pageSize }, onPaginationModelChange: (model) => {
                    setPageNumber(model.page + 1);
                    setPageSize(model.pageSize);
                }, pageSizeOptions: PAGE_SIZE_OPTIONS, onRowClick: (params, event) => {
                    const target = event.target;
                    if (target.closest('[data-field="actions"]') ||
                        target.closest("button") ||
                        target.closest('[role="button"]')) {
                        return;
                    }
                    // AP profile lists nodes by simulated connection, so navigate there.
                    const ap = params.row.deviceId;
                    if (ap)
                        navigate(`/dashboard/${ap}`);
                } }))));
};
export default FreeRoamingNodesPage;
